import { FormEvent, useCallback, useEffect, useState } from 'react';
import { PasswordInputRow } from './PasswordInputRow';
import { AuthGoogleSignInBlock } from './AuthGoogleSignInBlock';
import { LOGIN_OPTIONS, type LoginOption } from '../login/loginConstants';
import { isGoogleHostedEmail } from '../login/isGoogleHostedEmail';
import { resolveGoogleOAuthClientId } from '../login/googleSignIn';
import { isGoogleSignInSupportedOnThisDevice } from '../login/nativeGoogleSignIn';
import { useLoginForm } from '../login/useLoginForm';
import { translateLoginOption, useI18n } from '../i18n';
import directionStyles from './formInputDirection.module.css';
import styles from './AuthCredentialsSection.module.css';

export type AuthCredentialsSectionProps = {
  fixedLoginOption?: LoginOption;
  allowedLoginOptions?: readonly LoginOption[];
  /** When set, appended in parentheses after the credentials title (e.g. private client). */
  roleLabelInTitle?: LoginOption;
  /** Gmail addresses suggest Google sign-in above (main private-client portal). */
  enableGoogleEmailPrefer?: boolean;
  /** Google sign-in for the selected login type (register / business-owner login pages). */
  enableGoogleSignIn?: boolean;
  /** When false, hide the role-specific Google hint above the button (asset-owner login). */
  showGoogleIntro?: boolean;
  googleConfigured?: boolean;
  googleSubmitting?: boolean;
  onClearGoogleErrors?: () => void;
  onForgotPassword?: (loginType: LoginOption) => void;
};

export function AuthCredentialsSection({
  fixedLoginOption,
  allowedLoginOptions,
  roleLabelInTitle,
  enableGoogleEmailPrefer = false,
  enableGoogleSignIn = false,
  showGoogleIntro = true,
  googleConfigured = false,
  googleSubmitting = false,
  onClearGoogleErrors,
  onForgotPassword,
}: AuthCredentialsSectionProps) {
  const { t } = useI18n();
  const [showPasswordForGmail, setShowPasswordForGmail] = useState(false);
  const {
    isLoggedIn,
    loginBlockedTitle,
    selectedOption,
    fixedLoginOption: lockedOption,
    allowedLoginOptions: allowedOptions,
    onOptionChange,
    payload,
    setPayload,
    submitting,
    loginErrorFriendly,
    loginFieldKey,
    onSubmit,
  } = useLoginForm({ portalMode: true, fixedLoginOption, allowedLoginOptions });

  const loginTypeOptions =
    allowedOptions ??
    (lockedOption ? [lockedOption] : (LOGIN_OPTIONS as readonly LoginOption[]));

  const showLoginTypePicker = loginTypeOptions.length > 1;

  const titleRole = roleLabelInTitle ?? lockedOption ?? selectedOption;
  const credentialsTitle = roleLabelInTitle || lockedOption
    ? `${t('auth.portal.credentialsTitle')} (${translateLoginOption(titleRole, t)})`
    : t('auth.portal.credentialsTitle');

  const showGoogleEmailHint = isGoogleHostedEmail(payload.identifier.trim());
  const googleAvailable = Boolean(resolveGoogleOAuthClientId()) && isGoogleSignInSupportedOnThisDevice();
  const preferGoogleSignIn =
    enableGoogleEmailPrefer && showGoogleEmailHint && googleAvailable && !showPasswordForGmail;

  const onIdentifierChange = useCallback(
    (identifier: string) => {
      onClearGoogleErrors?.();
      setPayload((current) => ({ ...current, identifier }));
    },
    [onClearGoogleErrors, setPayload],
  );

  useEffect(() => {
    if (!showGoogleEmailHint) {
      setShowPasswordForGmail(false);
    }
  }, [showGoogleEmailHint]);

  const onUsePasswordInstead = useCallback(() => {
    setShowPasswordForGmail(true);
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    void onSubmit(event);
  };

  return (
    <section className={styles.section} aria-labelledby="auth-credentials-title">
      {enableGoogleSignIn ? (
        <AuthGoogleSignInBlock
          loginType={selectedOption}
          introKey={showGoogleIntro ? 'auth.portal.roleGoogleHint' : undefined}
        />
      ) : null}
      <h2 id="auth-credentials-title" className={styles.title}>
        {credentialsTitle}
      </h2>
      <form className={styles.form} onSubmit={handleSubmit} autoComplete="off">
        {showLoginTypePicker ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="auth-login-type">
              {t('login.loginType')}
            </label>
            <select
              id="auth-login-type"
              className={styles.select}
              value={selectedOption}
              onChange={(event) => onOptionChange(event.target.value as LoginOption)}
              disabled={isLoggedIn}
              title={isLoggedIn ? loginBlockedTitle : undefined}
            >
              {loginTypeOptions.map((option) => (
                <option key={option} value={option}>
                  {translateLoginOption(option, t)}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="auth-identifier">
            {t('auth.portal.username')}
          </label>
          <input
            key={`auth-identifier-${loginFieldKey}`}
            id="auth-identifier"
            className={`${styles.input} ${directionStyles.inputLtr}`}
            type="text"
            dir="ltr"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={payload.identifier}
            disabled={isLoggedIn}
            title={isLoggedIn ? loginBlockedTitle : undefined}
            onChange={(event) => onIdentifierChange(event.target.value)}
          />
        </div>

        {enableGoogleEmailPrefer && showGoogleEmailHint && googleAvailable ? (
          <p className={styles.googleEmailHint}>{t('auth.portal.googleEmailHintPrivate')}</p>
        ) : null}

        {preferGoogleSignIn ? (
          <button type="button" className={styles.passwordFallbackLink} onClick={onUsePasswordInstead}>
            {t('auth.portal.usePasswordInstead')}
          </button>
        ) : (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="auth-password">
              {t('login.password')}
            </label>
            <PasswordInputRow
              id="auth-password"
              inputKey={loginFieldKey}
              value={payload.password}
              onChange={(password) => setPayload((p) => ({ ...p, password }))}
              inputClassName={styles.input}
              toggleClassName={styles.passwordToggle}
              autoComplete="current-password"
              disabled={isLoggedIn}
              forceLtr
            />
          </div>
        )}

        {!preferGoogleSignIn && !isLoggedIn && onForgotPassword ? (
          <button
            type="button"
            className={styles.passwordFallbackLink}
            onClick={() => onForgotPassword(selectedOption)}
          >
            {t('auth.forgotPassword.link')}
          </button>
        ) : null}

        {loginErrorFriendly && !preferGoogleSignIn ? (
          <div className={styles.failureBlock} role="alert">
            <p className={styles.friendlyError}>{loginErrorFriendly}</p>
          </div>
        ) : null}

        <button
          type="submit"
          className={styles.submitBtn}
          disabled={submitting || googleSubmitting || isLoggedIn || preferGoogleSignIn}
          title={isLoggedIn ? loginBlockedTitle : undefined}
        >
          {submitting ? t('common.submitting') : t('login.login')}
        </button>
      </form>
    </section>
  );
}
